import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ShimmerThumbnail } from "react-shimmer-effects";
import TOPBAR from "../../components/TopBar";
import SEARCHBAR from "../../components/SeachBar";
import MENUBAR from "../../components/Menu";
import FOOTER from "../../components/Footer";
import { Helmet } from "react-helmet";

export default function LugNutTorqueChart() {
    const mainUrl = process.env.REACT_APP_EXPRESS_APP_URL;
    const navigate = useNavigate();

    const [isHidden, setIsHidden] = useState(false);
    const [showMenuSide, setShowMenuSide] = useState("");
    const [cartItems, setCartItems] = useState("");
    const [torqueData, setTorqueData] = useState([]);

    const [loading, setLoading] = useState(true);

    /*
     * Fetch torque data
     */
    useEffect(() => {
        fetchTorqueData();
    }, []);

    const fetchTorqueData = async () => {
        try {
            setLoading(true);

            const response = await axios.get(
                `${mainUrl}/api/lug-torque-chart`
            );

            if (response.data.success) {
                setTorqueData(response.data.data || []);
            }
        } catch (error) {
            console.error(
                "Error fetching torque chart:",
                error
            );
        } finally {
            setLoading(false);
        }
    };

    /*
     * Get cart items
     */
    useEffect(() => {
        const storedCartItems =
            localStorage.getItem("totalCartItems");

        if (storedCartItems) {
            try {
                const parsedCartItems =
                    JSON.parse(storedCartItems);

                const numCartItems =
                    Object.values(parsedCartItems);

                const sum = numCartItems.reduce(
                    (acc, val) => acc + Number(val),
                    0
                );

                setCartItems(sum);
            } catch (error) {
                console.error(
                    "Error parsing cart items:",
                    error
                );
            }
        }

        const timer = setTimeout(() => {
            setIsHidden(true);
        }, 4000);

        return () => clearTimeout(timer);
    }, []);

    /*
     * Menu sidebar
     */
    const setValueMenuSideBar = (value) => {
        setShowMenuSide(value);
    };

    /*
     * Current URL
     */
    const currentLink = window.location.href;
    const url = new URL(currentLink);
    const updatedUrl = url.toString();

    /*
     * Group data by Make
     */
    const groupedTorqueData = useMemo(() => {
        const groups = {};

        torqueData.forEach((item) => {
            if (!item.make) {
                return;
            }

            if (!groups[item.make]) {
                groups[item.make] = [];
            }

            groups[item.make].push(item);
        });

        return groups;
    }, [torqueData]);

    /*
     * Create brand summary
     *
     * Model Count = unique models
     * Year Range = minimum year - maximum year
     */
    const brandSummary = useMemo(() => {
        return Object.keys(groupedTorqueData)
            .map((make) => {
                const brandData =
                    groupedTorqueData[make] || [];

                /*
                 * Get unique models
                 */
                const uniqueModels = [
                    ...new Set(
                        brandData
                            .map((item) =>
                                String(
                                    item.model || ""
                                )
                                    .trim()
                                    .toLowerCase()
                            )
                            .filter(Boolean)
                    ),
                ];

                /*
                 * Get all valid years
                 */
                const years = brandData
                    .map((item) =>
                        parseInt(item.year, 10)
                    )
                    .filter(
                        (year) =>
                            !isNaN(year)
                    );

                let yearRange = "-";

                if (years.length > 0) {
                    const minYear = Math.min(
                        ...years
                    );

                    const maxYear = Math.max(
                        ...years
                    );

                    yearRange =
                        minYear === maxYear
                            ? `${minYear}`
                            : `${minYear} - ${maxYear}`;
                }

                return {
                    make,
                    modelCount:
                        uniqueModels.length,
                    yearRange,
                };
            })
            .sort((a, b) =>
                a.make.localeCompare(b.make)
            );
    }, [groupedTorqueData]);

    /*
     * Navigate to brand detail page
     */
    const handleViewBrand = (make) => {
        const brandSlug = make
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "");

        navigate(
            `/lug-nut-torque-chart/${brandSlug}`
        );
    };

    return (
        <>
            <Helmet>
                <title>
                    Lug Nut Torque Chart
                </title>

                <meta
                    name="description"
                    content="Lug nut torque chart with proper torque specifications for different vehicles."
                />

                <link
                    rel="canonical"
                    href={updatedUrl}
                />
            </Helmet>

            <TOPBAR isHidden={isHidden} />

            <SEARCHBAR
                setValueMenuSideBar={
                    setValueMenuSideBar
                }
                cartItemsdata={cartItems}
            />

            <MENUBAR
                showMenuSide={showMenuSide}
                setValueMenuSideBar={
                    setValueMenuSideBar
                }
            />

            <div className="parformane-bar"
                style={{ background: "#494949" }}
            >
                <div className="container d-flex justify-content-between">
                    <ul className="p-0 m-0 listing-parmofamne">
                        <li><a href="/">Home /</a></li>
                        <li className='active-bread text-decoration-none'><a href="#">Lug Nut Torque Chart</a></li>
                    </ul>

                </div>
            </div>

            <section className="tech-center-page">
                <div className="container">

                    <h1 className="text-center heading mb-4">
                        Lug Nut Torque Chart
                    </h1>
                    <p className="text-center mb-5">
                        When installing wheels, it is important to use the manufacturer-recommended lug nut torque specifications. Proper torque helps ensure the wheels are securely fastened and prevents lug nuts from being too loose or over-tightened. Always check your vehicle’s recommended torque and use a properly calibrated torque wrench.
                    </p>

                    <div className="table-responsive">
                        <table className="table table-bordered table-striped align-middle">

                            <thead className="table-dark">
                                <tr>
                                    <th>
                                        Brand Name
                                    </th>

                                    <th className="text-center">
                                        Models
                                    </th>

                                    <th className="text-center">
                                        Year Range
                                    </th>

                                    <th
                                        className="text-center"
                                        style={{
                                            width: "150px",
                                        }}
                                    >
                                        Action
                                    </th>
                                </tr>
                            </thead>

                            <tbody>

                                {/* Shimmer Loading */}
                                {loading ? (
                                    Array.from({
                                        length: 8,
                                    }).map((_, idx) => (
                                        <tr key={idx}>

                                            {/* Brand Name */}
                                            <td>
                                                <ShimmerThumbnail
                                                    height={20}
                                                    rounded
                                                    className="mb-0"
                                                />
                                            </td>

                                            {/* Models */}
                                            <td className="text-center">
                                                <ShimmerThumbnail
                                                    height={20}
                                                    rounded
                                                    className="mb-0"
                                                />
                                            </td>

                                            {/* Year Range */}
                                            <td className="text-center">
                                                <ShimmerThumbnail
                                                    height={20}
                                                    rounded
                                                    className="mb-0"
                                                />
                                            </td>

                                            {/* Action */}
                                            <td className="text-center">
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        justifyContent:
                                                            "center",
                                                    }}
                                                >
                                                    <ShimmerThumbnail
                                                        height={30}
                                                        width={70}
                                                        rounded
                                                        className="mb-0"
                                                    />
                                                </div>
                                            </td>

                                        </tr>
                                    ))
                                ) : brandSummary.length > 0 ? (
                                    brandSummary.map(
                                        (brand) => (
                                            <tr
                                                key={
                                                    brand.make
                                                }
                                            >
                                                <td>
                                                    {
                                                        brand.make
                                                    }
                                                </td>

                                                <td className="text-center">
                                                    {
                                                        brand.modelCount
                                                    }
                                                </td>

                                                <td className="text-center">
                                                    {
                                                        brand.yearRange
                                                    }
                                                </td>

                                                <td className="text-center">
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            handleViewBrand(
                                                                brand.make
                                                            )
                                                        }
                                                        style={{
                                                            fontSize: "13px",
                                                            fontWeight: 600,
                                                            letterSpacing: "0.5px",
                                                            padding: "5px 14px",
                                                            borderRadius: "4px",
                                                            border: "none",
                                                            background: "#e81c2e",
                                                            color: "#fff",
                                                            cursor: "pointer",
                                                            transition: "background 0.15s",
                                                        }}
                                                        onMouseEnter={(e) =>
                                                        (e.currentTarget.style.background =
                                                            "#c8172a")
                                                        }
                                                        onMouseLeave={(e) =>
                                                        (e.currentTarget.style.background =
                                                            "#e81c2e")
                                                        }
                                                    >
                                                        View
                                                    </button>
                                                </td>
                                            </tr>
                                        )
                                    )
                                ) : (
                                    <tr>
                                        <td
                                            colSpan="4"
                                            className="text-center py-4"
                                        >
                                            No torque chart
                                            data found.
                                        </td>
                                    </tr>
                                )}

                            </tbody>

                        </table>
                    </div>

                </div>
            </section>

            <FOOTER />
        </>
    );
}














// import React, { useState, useEffect, useMemo } from "react";
// import axios from "axios";
// import TOPBAR from "../../components/TopBar";
// import SEARCHBAR from "../../components/SeachBar";
// import MENUBAR from "../../components/Menu";
// import FOOTER from "../../components/Footer";
// import { Helmet } from "react-helmet";

// export default function LugNutTorqueChart() {
//     const mainUrl = process.env.REACT_APP_EXPRESS_APP_URL;

//     const [isHidden, setIsHidden] = useState(false);
//     const [showMenuSide, setShowMenuSide] = useState("");
//     const [cartItems, setCartItems] = useState("");
//     const [torqueData, setTorqueData] = useState([]);

//     const [selectedBrand, setSelectedBrand] = useState("");
//     const [expandedMakes, setExpandedMakes] = useState({});

//     const [torqueError, setTorqueError] = useState("");


//     useEffect(() => {
//         fetchTorqueData();
//     }, []);

//     const fetchTorqueData = async () => {
//         try {
//             const response = await axios.get(`${mainUrl}/api/lug-torque-chart`);
//             if (response.data.success) {
//                 setTorqueData(response.data.data);
//             }
//         } catch (error) {
//             console.error("Error fetching torque chart", error);
//         }
//     };

//     useEffect(() => {
//         const storedCartItems = localStorage.getItem('totalCartItems');
//         if (storedCartItems) {
//             const parsedCartItems = JSON.parse(storedCartItems);
//             const numCartItems = Object.values(parsedCartItems);
//             const sum = numCartItems.reduce((acc, val) => acc + val, 0);
//             setCartItems(sum);
//         }
//         setTimeout(() => {
//             setIsHidden(true);
//         }, 4000);
//     }, []);

//     const setValueMenuSideBar = (value) => {
//         setShowMenuSide(value);
//     };

//     const currentLink = window.location.href;
//     const url = new URL(currentLink);
//     const updatedUrl = url.toString();

//     // Group data by Make
//     const groupedTorqueData = useMemo(() => {
//         const groups = {};
//         torqueData.forEach((item) => {
//             if (!groups[item.make]) {
//                 groups[item.make] = [];
//             }
//             groups[item.make].push(item);
//         });
//         return groups;
//     }, [torqueData]);

//     // All makes sorted alphabetically
//     const allMakes = useMemo(() => {
//         return Object.keys(groupedTorqueData).sort((a, b) => a.localeCompare(b));
//     }, [groupedTorqueData]);

//     // Handle brand selection from dropdown
//     const handleSelectBrand = (e) => {
//         const make = e.target.value;
//         setSelectedBrand(make);

//         if (make) {
//             setExpandedMakes((prev) => ({
//                 ...prev,
//                 [make]: true,
//             }));

//             // Scroll smoothly to the selected brand
//             setTimeout(() => {
//                 const element = document.getElementById(`brand-${make}`);
//                 if (element) {
//                     element.scrollIntoView({ behavior: "smooth", block: "start" });
//                 }
//             }, 100);
//         }
//     };

//     const toggleExpanded = (make) => {
//         setExpandedMakes((prev) => ({
//             ...prev,
//             [make]: !prev[make],
//         }));
//     };

//     const handleTorqueNavigation = async (e, item) => {
//         e.preventDefault();

//         // Clear previous error
//         setTorqueError("");

//         try {
//             const response = await axios.get(
//                 `${mainUrl}/api/lug-torque-chart/resolve-vehicle`,
//                 {
//                     params: {
//                         year: item.year,
//                         make: item.make,
//                         model: item.model,
//                         trim: item.trim,
//                     },
//                 }
//             );

//             if (!response.data.success || !response.data.data) {
//                 setTorqueError(response.data.message || "Vehicle not found");
//                 return;
//             }

//             const vehicle = response.data.data;

//             const vehicalDetails = {
//                 year: String(vehicle.year),
//                 make: vehicle.make,
//                 model: vehicle.model,
//                 variant: vehicle.variant,
//                 make_name: vehicle.make_name,
//                 model_name: vehicle.model_name,
//                 variant_name: vehicle.variant_name,
//             };

//             localStorage.setItem(
//                 "vehical_details",
//                 JSON.stringify(vehicalDetails)
//             );

//             window.location.href = "/";
//         } catch (error) {
//             console.error("Error resolving torque vehicle:", error);

//             setTorqueError(
//                 error.response?.data?.message ||
//                 "Unable to find this vehicle. Please try again."
//             );
//         }
//     };

//     return (
//         <>
//             <Helmet>
//                 <title>Lug Nut Torque Chart</title>
//                 <meta name="description" content="Lug nut torque chart with proper torque specifications for different vehicles." />
//                 <link rel="canonical" href={updatedUrl} />
//             </Helmet>

//             <TOPBAR isHidden={isHidden} />
//             <SEARCHBAR setValueMenuSideBar={setValueMenuSideBar} cartItemsdata={cartItems} />
//             <MENUBAR showMenuSide={showMenuSide} setValueMenuSideBar={setValueMenuSideBar} />

//             <section className="tech-center-page">
//                 <div className="container">
//                     <h1 className="text-center heading mb-4">Lug Nut Torque Chart</h1>

//                     <div className="d-flex align-items-center gap-3 mb-4 flex-wrap justify-content-center justify-content-md-end">
//                         <span className="fw-bold" style={{ whiteSpace: "nowrap" }}>
//                             Search for Brands
//                         </span>

//                         <div style={{ minWidth: "280px" }}>
//                             <select
//                                 className="form-select"
//                                 value={selectedBrand}
//                                 onChange={handleSelectBrand}
//                             >
//                                 <option value="">Select Brand</option>
//                                 {allMakes.map((make) => (
//                                     <option key={make} value={make}>
//                                         {make}
//                                     </option>
//                                 ))}
//                             </select>
//                         </div>
//                     </div>

//                     {/* Error Message */}
//                     {torqueError && (
//                         <div
//                             className="alert alert-danger mb-4"
//                             role="alert"
//                         >
//                             {torqueError}
//                         </div>
//                     )}

//                     {/* Brand Accordion List */}
//                     <div className="accordion-list">
//                         {allMakes.map((make) => {
//                             const isExpanded = !!expandedMakes[make];
//                             const brandData = groupedTorqueData[make] || [];

//                             return (
//                                 <div
//                                     key={make}
//                                     id={`brand-${make}`}
//                                     className="mb-3 border rounded overflow-hidden shadow-sm"
//                                 >
//                                     {/* Brand Header */}
//                                     <div
//                                         className="d-flex justify-content-between align-items-center px-4 py-3 bg-light"
//                                         style={{ backgroundColor: "#f8f9fa", fontWeight: "bold", cursor: "pointer", color: "#555" }}
//                                         onClick={() => toggleExpanded(make)}
//                                     >
//                                         <h5 className="mb-0 text-uppercase" style={{ fontSize: "16px" }}>{make}</h5>
//                                         <span className="fs-6 transition">
//                                             {isExpanded ? "▲" : "▼"}
//                                         </span>
//                                     </div>

//                                     <div
//                                         className="table-responsive"
//                                         style={{ display: isExpanded ? "block" : "none" }}
//                                     >
//                                         <table className="table table-bordered mb-0">
//                                             <thead className="table-dark">
//                                                 <tr>
//                                                     <th>Make</th>
//                                                     <th>Model</th>
//                                                     <th>Trim</th>
//                                                     <th>Year</th>
//                                                     <th>Torque</th>
//                                                 </tr>
//                                             </thead>
//                                             <tbody>
//                                                 {brandData.map((item, index) => (
//                                                     <tr key={index}>
//                                                         <td>{index === 0 ? item.make : ""}</td>
//                                                         <td>{item.model}</td>
//                                                         <td>{item.trim}</td>
//                                                         <td>{item.year}</td>
//                                                         {/* <td>{item.torque}</td> */}
//                                                         <td>
//                                                             <span>{item.torque}</span>

//                                                             {index < 40 && (
//                                                                 <a
//                                                                     href="/"
//                                                                     className="ms-2"
//                                                                     title="Select this vehicle"
//                                                                     aria-label={`Select ${item.make} ${item.model} ${item.trim} ${item.year}`}
//                                                                     onClick={(e) => handleTorqueNavigation(e, item)}
//                                                                 >
//                                                                     <i className="fa fa-external-link" aria-hidden="true"></i>
//                                                                 </a>
//                                                             )}
//                                                         </td>
//                                                     </tr>
//                                                 ))}
//                                             </tbody>
//                                         </table>
//                                     </div>
//                                 </div>
//                             );
//                         })}
//                     </div>
//                 </div>
//             </section>

//             <FOOTER />
//         </>
//     );
// }